import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import * as inflection from 'inflection';
import React, { useEffect, useState } from 'react';
import OrganizationOnboardingStep from './OrganizationOnboardingStep';
import * as styles from './OrganizationOnboardingStepInvite.module.scss';
import {
  ProvisionTypeSelectOption,
  ProvisionTypeSelectSingleValue,
} from '../../admin/provisions/ProvisionTypeSelectOption';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Buttons from '../../components/Buttons';
import { Field, FieldSet, Submit } from '../../components/Form';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { ProvisionType } from '../../graphql/generated/graphql';
import caretIcon from '../../icons/non-prefilled/16x16/caret.svg';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import type { InvitationUserSelectOptionType } from '../../invitation/InvitationUserSelect';
import InvitationUserSelect from '../../invitation/InvitationUserSelect';
import useInviteMutation from '../../invitation/useInviteMutation';
import type { ToastLevel } from '../../ui/uiSlice';
import { UIActions } from '../../ui/uiSlice';
import handleApolloError from '../../utils/error/handleApolloError';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import provisionRegisterUrl from '../../utils/url/provisionRegisterUrl';

const ONBOARDING_STEP_INVITE_PROVISION_FRAGMENT = gql(`
  fragment OnboardingStepInvite_provision on Provision {
    id
    name
    type
    ...provisionRegisterUrl_provision
  }
`);

const ONBOARDING_INVITE_QUERY = gql(/** @complexity 4 */ `
  query OrganizationOnboardingStepInvite {
    session: currentSession {
      organization {
        id
        provisions {
          ...OnboardingStepInvite_provision
        }
      }
    }
  }
`);

type Values = {
  invites: InvitationUserSelectOptionType[];
  type: ProvisionType;
  message: string;
};

interface InviteFieldsProps {
  provisions?: DocumentType<typeof ONBOARDING_STEP_INVITE_PROVISION_FRAGMENT>[];
}

const InviteFields = ({ provisions }: InviteFieldsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { values, setFieldValue } = useFormikContext<Values>();
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setIsLinkCopied(false);
  }, [values.type]);

  const selectedProvision = provisions?.find(
    (provision) => provision.type === values.type,
  );
  const currentProvisionsUrl = selectedProvision
    ? provisionRegisterUrl(selectedProvision)
    : null;

  const handleCopyButtonClick = async () => {
    if (currentProvisionsUrl) {
      await navigator.clipboard.writeText(currentProvisionsUrl);
      setIsLinkCopied(true);
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Shareable link copied',
        }),
      );
    }
  };

  return (
    <>
      <div className={styles.invitesLegend}>
        <div className={styles.invitesLegendText}>To:</div>
        {currentProvisionsUrl && !isLinkCopied && (
          <Button
            className={styles.provisionLink}
            onClick={handleCopyButtonClick}
          >
            <SVGIcon className={styles.linkIcon} size={10}>
              {linkIcon}
            </SVGIcon>
            Copy shareable link
          </Button>
        )}
        {isLinkCopied && (
          <div className={styles.provisionLink}>
            <SVGIcon className={styles.linkIcon} size={10}>
              {checkIcon}
            </SVGIcon>
            Link copied
          </div>
        )}
      </div>
      <FieldSet>
        <InvitationUserSelect
          value={values.invites}
          onChange={(invites) => {
            setFieldValue('invites', invites);
          }}
          placeholder="Enter a name or email of a teammate"
        />
      </FieldSet>
      {!isExpanded && (
        <>
          <div className={styles.note}>
            Teammates will be invited as{' '}
            <strong>{selectedProvision?.name}</strong> unless you customize the
            invitation
          </div>
          <Button variant="secondary" block onClick={() => setIsExpanded(true)}>
            Show additional settings
            <SVGIcon className={styles.buttonCaret} size={8}>
              {caretIcon}
            </SVGIcon>
          </Button>
        </>
      )}
      {isExpanded && (
        <div
          className={classNames(styles.expandableSection, {
            [styles.expanded]: isExpanded,
          })}
        >
          <Field
            label="Role"
            type="select"
            name="type"
            isClearable={false}
            options={[
              {
                description: 'Standard team seat',
                value: ProvisionType.User,
              },
              {
                description: 'Unlocks all privileges',
                value: ProvisionType.Admin,
              },
              {
                description: 'Limited access to topics',
                value: ProvisionType.Guest,
              },
            ]}
            components={{
              Option: ProvisionTypeSelectOption,
              SingleValue: ProvisionTypeSelectSingleValue,
            }}
          />
          <Field
            autoFocus
            type="textarea"
            name="message"
            rows={3}
            label="Custom Message"
            placeholder="What should your teammates know about this invite?"
          />
        </div>
      )}
    </>
  );
};

interface OrganizationOnboardingStepInviteProps {
  lastStep: boolean;
  step: number;
  onSkip: () => void;
  onNext: () => void;
  onPrevious: (() => void) | null;
}

const OrganizationOnboardingStepInvite = (
  props: OrganizationOnboardingStepInviteProps,
) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsSize(Sizes.SM);

  const { data: { session } = {} } = useQuery(ONBOARDING_INVITE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const provisions = session?.organization.provisions ?? [];

  const invite = useInviteMutation();

  const onSubmit = async ({
    invites,
    type,
    message,
  }: Values): Promise<boolean> => {
    const provisionId = provisions.find(
      (provision) => provision.type === type,
    )?.id;
    if (!provisionId) {
      // A new organization always have prefilled provisions.
      return false;
    }
    const results = await Promise.all(
      invites.map(async ({ value: email, label }) => {
        try {
          await invite({
            variables: {
              email,
              name: label !== email ? label : null,
              provisionId,
              message,
            },
          });
          return true;
        } catch (error) {
          const getHandler =
            (toastMessage: string, level: ToastLevel = 'warning') =>
            () =>
              dispatch(UIActions.pushToast({ level, message: toastMessage }));

          handleApolloError(error, {
            USER_EXISTS: getHandler(`${label} is already on your team.`),
            USER_INVITED: getHandler(`${label} is already invited.`),
            RATE_LIMIT: getHandler(
              `You sent too many invites. ${label} has not been invited!`,
              'error',
            ),
          });
          return false;
        }
      }),
    );

    const sentInvites = invites.filter((_, index) => results[index]);
    const totalSent = sentInvites.length;
    if (totalSent > 0) {
      dispatch(
        UIActions.pushToast({
          message: `Invitations sent to ${totalSent} ${inflection.inflect(
            'teammate',
            totalSent,
          )}.`,
          level: 'success',
        }),
      );
    }
    return totalSent === results.length;
  };

  return (
    <OrganizationOnboardingStep<Values>
      {...props}
      onPrevious={isMobile ? null : props.onPrevious}
      // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
      nextSection={(values: Values) => (
        <Buttons>
          <Button
            variant="secondary"
            size="large"
            onClick={() => props.onNext()}
          >
            Skip
          </Button>
          <Submit
            variant="primary"
            size="large"
            disabled={values.invites.length === 0}
          >
            Invite {values.invites.length}{' '}
            {inflection.inflect('teammate', values.invites.length)}
          </Submit>
        </Buttons>
      )}
      initialValues={{ invites: [], type: ProvisionType.User, message: '' }}
      title="Invite teammates"
      onSubmit={onSubmit}
    >
      <InviteFields provisions={provisions} />
    </OrganizationOnboardingStep>
  );
};

export default OrganizationOnboardingStepInvite;
